import React, { useState } from 'react';
import { FlexibleCRUDTable } from '../../components/common/FlexibleCRUDTable';
import { emailTemplateEntity } from '../../config/entities/emailTemplates';
import { crmService } from '../../services/crmCRUD';
import { useLanguage } from '../../state/language/LanguageProvider';
// Removed CreateTemplateModal - using EditModal instead
import { ViewModal } from '../../components/common/ViewModal';
import { EditModal } from '../../components/common/EditModal';

export const EmailTemplatesPage: React.FC = () => {
  const { t } = useLanguage();
  
  // Modal state management
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Use Dedicated CRM Service (same as dashboard) with wrapper for FlexibleCRUDTable compatibility
  const templateService = {
    getAll: (_params: any) => crmService.getEmailTemplates(),
    get: (id: string) => crmService.getEmailTemplateById(id),
    create: (data: any) => crmService.createEmailTemplate(data),
    update: (id: string, data: any) => crmService.updateEmailTemplate(id, data),
    delete: (id: string) => crmService.deleteEmailTemplate(id)
  };

  // CRUD handlers
  const handleViewTemplate = (template: any) => {
    setSelectedTemplate(template);
    setIsViewModalOpen(true);
  };

  const handleEditTemplate = (template: any) => {
    setSelectedTemplate(template);
    setIsEditModalOpen(true);
  };

  const handleDeleteTemplate = async (template: any) => {
    if (window.confirm(t('crm.templates.confirmDelete', { name: template.name }))) {
      try {
        await templateService.delete(template.id);
        // The table will automatically refresh
      } catch (error) {
        console.error('Error deleting template:', error);
        alert(t('crm.templates.deleteError'));
      }
    }
  };

  const handleCreateTemplate = () => {
    setIsCreateModalOpen(true);
  };

  const handleSaveTemplate = async (data: any) => {
    try {
      await templateService.update(selectedTemplate.id, data);
      setIsEditModalOpen(false);
      setSelectedTemplate(null);
    } catch (error) {
      console.error('Error updating template:', error);
      throw error; // Re-throw so the modal can handle the error
    }
  };

  const handleCreateNewTemplate = async (template: any) => {
    try {
      await templateService.create(template);
      setIsCreateModalOpen(false);
    } catch (error) {
      console.error('Error creating template:', error);
      throw error; // Re-throw so the modal can handle the error
    }
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedTemplate(null);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedTemplate(null);
  };

  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full py-6">
        {/* Main Content */}
        <div className="px-4 py-6">
          <FlexibleCRUDTable
            entity={emailTemplateEntity}
            service={templateService}
            onView={handleViewTemplate}
            onEdit={handleEditTemplate}
            onDelete={handleDeleteTemplate}
            onCreate={handleCreateTemplate}
            onOperationComplete={() => {
              // Table will automatically refresh after operations
            }}
            searchPlaceholder={t('crm.templates.searchPlaceholder')}
            showPagination={true}
            showSearch={true}
          />
        </div>
      </div>

      {/* View Template Modal */}
      {isViewModalOpen && selectedTemplate && (
        <ViewModal
          isOpen={isViewModalOpen}
          onClose={handleCloseViewModal}
          title={`View ${emailTemplateEntity.displayNameSingular}`}
          data={selectedTemplate}
          fields={emailTemplateEntity.fields.filter(f => !f.hidden).map(f => ({
            key: f.key || f.name || '',
            label: f.label,
            type: f.type
          }))}
        />
      )}

      {/* Edit Template Modal */}
      {isEditModalOpen && selectedTemplate && (
        <EditModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          title={`Edit ${emailTemplateEntity.displayNameSingular}`}
          entity={emailTemplateEntity}
          initialData={selectedTemplate}
          onSubmit={handleSaveTemplate}
          onCancel={handleCloseEditModal}
          loading={false}
        />
      )}

      {/* Create Template Modal */}
      {isCreateModalOpen && (
        <EditModal
          isOpen={isCreateModalOpen}
          onClose={handleCloseCreateModal}
          title={`Create ${emailTemplateEntity.displayNameSingular}`}
          entity={emailTemplateEntity}
          onSubmit={handleCreateNewTemplate}
          onCancel={handleCloseCreateModal}
          loading={false}
        />
      )}
    </div>
  );
};